import {
  ExclamationCircleFilled, PlusOutlined, ScheduleOutlined, AppstoreOutlined,
  PercentageOutlined, TagsOutlined, CalendarOutlined
} from "@ant-design/icons";
import { Button, Card, Modal, Space, Table, Typography, Tag, Row, Col } from "antd";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import AddFractionnement from "./AddFractionnement";
import ActionDropdown from "../../production/components/ActionDropdown";
import axiosInstance from "../../../variables/api/axiosInstance";
import { useNavigate } from "react-router-dom";
import TauxFractionnement from "./TauxFractionnement";
import { StatCard } from "../../../components/statistics/StatCard";
import { COLORS } from "../../../components/ui/colors";

const { confirm } = Modal;

const { Title, Text } = Typography;

const periodeColor = { JOUR: "cyan", MOIS: "blue", AN: "geekblue" };
const periodeLabel = (p) => (p === "JOUR" ? "JOURS" : p || "MOIS");

const Fractionnements = () => {
  const [fractionnements, setFractionnements] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedFractionnement, setSelectedFractionnement] = useState(null);
  const [line, setLine] = useState(null);
  const [isModalOpenLine, setIsModalOpenLine] = useState(false);
  const [tauxFractionnements, setTauxFractionnements] = useState([]);

  const navigate = useNavigate();

  const handleShow = (edit, fractionnement = null) => {
    setEditMode(edit);
    setSelectedFractionnement(fractionnement);
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setSelectedFractionnement(null); // Clear selected fractionnement on close
  };

  useEffect(() => {
    fetchFractionnements();
  }, [isModalOpenLine]);

  const fetchFractionnements = () => {
    axiosInstance
      .get(`/api/fractionnement`)
      .then((res) => {
        // Sort from newest to oldest by ID
        const sorted = [...res.data].sort((a, b) => b.fractionnementId - a.fractionnementId);
        setFractionnements(sorted);
      })
      .catch((err) => {
        console.error("Error fetching fractionnements:", err);
        toast.error("Erreur lors du chargement des fractionnements");
      });
  };

  useEffect(() => {
    axiosInstance.get("/api/taux-fractionnement").then((res) => {
      setTauxFractionnements(res.data);
    }).catch((err) => {
      console.error("Error fetching taux fractionnements:", err);
    });
  }, [isModalOpenLine]);

  const columns = [
    {
      title: "Fractionnement",
      dataIndex: "libelleFractionnement",
      key: "libelleFractionnement",
      render: (val, record) => (
        <Space>
          <div style={{
            width: 38, height: 38, borderRadius: 10, display: "flex",
            alignItems: "center", justifyContent: "center",
            background: COLORS.gradient, color: "#fff", fontWeight: 700, fontSize: 14
          }}>
            {record.nombre}
          </div>
          <div>
            <Text strong style={{ color: COLORS.secondary, display: "block" }}>{val || "—"}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>{record.description || ""}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: "Versements",
      dataIndex: "nombre",
      key: "nombre",
      align: "center",
      render: (n) => (
        <Tag color={n > 1 ? "purple" : "default"} style={{ borderRadius: 12, fontWeight: 600 }}>
          {n > 1 ? `${n} versements` : "Paiement unique"}
        </Tag>
      ),
    },
    {
      title: "Période",
      key: "periode",
      align: "center",
      render: (_, record) => (
        <Tag color={periodeColor[record.periode] || "blue"} style={{ borderRadius: 12 }}>
          <CalendarOutlined style={{ marginRight: 4 }} />
          {record.moisMin} – {record.moisMax} {periodeLabel(record.periode)}
        </Tag>
      ),
    },
    {
      title: "Catégorie",
      dataIndex: "categorie",
      key: "categorie",
      align: "center",
      render: (cat) => cat
        ? <Tag color="geekblue" style={{ borderRadius: 12 }}><TagsOutlined style={{ marginRight: 4 }} />{cat.codeCategorie || cat.nom}</Tag>
        : <Tag style={{ borderRadius: 12 }}>Toutes</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <ActionDropdown
          actions={[
            {
              key: "edit",
              label: "Modifier",
              icon: <i className="bx bx-edit-alt" />,
              onClick: () => handleShow(true, record),
            },
            {
              key: "details",
              label: "Details",
              icon: <i className="bx bx-detail" />,
              onClick: () => navigate(`/taux-fractionnement/${record.fractionnementId}`),
            },
            {
              key: "delete",
              label: "Supprimer",
              icon: <i className="bx bx-trash" />,
              danger: true,
              onClick: () => showDeleteConfirm(record.fractionnementId),
            },
          ]}
        />
      ),
    },
  ];

  const tauxColumns = [
    {
      title: 'Rang',
      dataIndex: 'rang',
      key: 'rang',
      width: 90,
      align: 'center',
      render: (rang) => (
        <span style={{
          display: "inline-flex", alignItems: "center", justifyContent: "center",
          width: 30, height: 30, borderRadius: "50%",
          background: `${COLORS.secondary}15`, color: COLORS.secondary, fontWeight: 700,
        }}>
          {rang}
        </span>
      ),
    },
    {
      title: 'Libellé',
      dataIndex: 'libelleTauxFractionnement',
      key: 'libelleTauxFractionnement',
      render: (val) => <Text>{val || "—"}</Text>,
    },
    {
      title: 'Taux',
      dataIndex: 'taux',
      key: 'taux',
      width: 120,
      align: 'center',
      render: (_, record) => (
        <Tag color="green" style={{ borderRadius: 12, fontWeight: 600, fontSize: 13 }}>
          <PercentageOutlined style={{ marginRight: 4 }} />{record.taux} %
        </Tag>
      ),
    },
  ];

  const showDeleteConfirm = (fractionnementId) => {
    confirm({
      title: "Êtes-vous sûr de vouloir supprimer ce fractionnement?",
      icon: <ExclamationCircleFilled />,
      content: "Cette action est irréversible.",
      okText: "Oui",
      okType: "danger",
      cancelText: "Annuler",
      onOk() {
        deleteFractionnement(fractionnementId);
      },
    });
  };

  const deleteFractionnement = (fractionnementId) => {
    axiosInstance
      .delete(`/api/fractionnement/${fractionnementId}`)
      .then(() => {
        setFractionnements((prev) => prev.filter(f => f.fractionnementId !== fractionnementId));
        toast.success("Fractionnement supprimé avec succès");
      })
      .catch((err) => {
        console.error("Error deleting fractionnement:", err);
        toast.error("Erreur lors de la suppression");
      });
  };

  const handleSaved = () => {
    // Re-fetch all fractionnements to ensure the list is up-to-date
    // after an add or update operation.
    fetchFractionnements();
    handleClose();
  };

  const expandedTauxFractionnementRowRender = (record) => {
    const lignes = tauxFractionnements
      ?.filter((l) => l.fractionnement?.fractionnementId === record?.fractionnementId)
      ?.slice()
      .sort((a, b) => (a.rang || 0) - (b.rang || 0))
      .map((l) => ({
        key: l.tauxFractionnementId,
        rang: l.rang,
        libelleTauxFractionnement: l.libelleTauxFractionnement,
        taux: l.taux,
        data: l,
      })) || [];

    return (
      <Card
        type="inner"
        style={{
          margin: '12px 24px',
          background: '#F7F9FC',
          border: `1px solid ${COLORS.secondary}15`,
          borderRadius: 12,
          boxShadow: '0 1px 4px rgba(39,35,102,0.06)',
        }}
        bodyStyle={{ padding: 20 }}
      >
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginBottom: 16, flexWrap: "wrap", gap: 12,
        }}>
          <Space>
            <PercentageOutlined style={{ color: COLORS.secondary, fontSize: 18 }} />
            <Title level={5} style={{ margin: 0, color: COLORS.secondary }}>
              Taux de fractionnement
            </Title>
            <Tag color="blue" style={{ borderRadius: 12 }}>{lignes.length} taux</Tag>
          </Space>
          <Button
            icon={<PlusOutlined />}
            type="primary"
            onClick={() => {
              setEditMode(false);
              setLine(null);
              setSelectedFractionnement(record);
              setIsModalOpenLine(true);
            }}
            style={{ background: COLORS.gradient, border: "none", borderRadius: 8, fontWeight: 600 }}
          >
            Nouveau taux
          </Button>
        </div>

        {lignes.length > 0 ? (
          <Table
            columns={tauxColumns}
            rowClassName={(r) => (r.key === line?.tauxFractionnementId ? "night-blue-row" : "")}
            dataSource={lignes}
            pagination={false}
            size="middle"
            rowKey="key"
            style={{ background: "#fff", borderRadius: 10, overflow: "hidden" }}
            onRow={(lineRecord) => ({
              style: { cursor: "pointer" },
              onClick: () => {
                setEditMode(true);
                setSelectedFractionnement(record);
                setLine(lineRecord.data);
                setIsModalOpenLine(true);
              },
            })}
          />
        ) : (
          <Text type="secondary" style={{ display: "block", textAlign: "center", padding: "16px 0" }}>
            Aucun taux défini — cliquez sur « Nouveau taux » pour en ajouter un.
          </Text>
        )}
      </Card>
    );
  };


  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu={"TARIFICATION"} />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">

              {/* En-tête */}
              <div style={{
                background: COLORS.gradient,
                borderRadius: 16,
                padding: "28px 32px",
                marginBottom: 24,
                color: "#fff",
                boxShadow: "0 10px 24px rgba(36,33,102,0.25)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 16,
              }}>
                <div>
                  <Title level={2} style={{ color: "#fff", margin: 0, display: "flex", alignItems: "center", gap: 12 }}>
                    <ScheduleOutlined /> Fractionnements
                  </Title>
                  <Text style={{ color: "rgba(255,255,255,0.85)", fontSize: 15 }}>
                    Configuration des modalités de paiement et des taux par catégorie
                  </Text>
                </div>
                <Button
                  size="large"
                  icon={<PlusOutlined />}
                  onClick={() => handleShow(false)}
                  style={{
                    background: "#fff", color: COLORS.secondary, border: "none",
                    borderRadius: 10, height: 46, fontWeight: 600,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  }}
                >
                  Nouveau fractionnement
                </Button>
              </div>

              {/* Statistiques */}
              <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={8}>
                  <StatCard
                    title="Fractionnements"
                    value={fractionnements.length}
                    subtitle="Modalités configurées"
                    icon={<AppstoreOutlined style={{ color: "#fff", fontSize: 22 }} />}
                    iconBg={COLORS.gradient}
                  />
                </Col>
                <Col xs={24} sm={8}>
                  <StatCard
                    title="Taux définis"
                    value={tauxFractionnements.length}
                    subtitle="Lignes de taux par rang"
                    icon={<PercentageOutlined style={{ color: "#fff", fontSize: 22 }} />}
                    iconBg="linear-gradient(135deg,#13c2c2 0%,#242166 100%)"
                  />
                </Col>
                <Col xs={24} sm={8}>
                  <StatCard
                    title="Catégories couvertes"
                    value={new Set(fractionnements.filter(f => f.categorie).map(f => f.categorie.categorieId)).size}
                    subtitle="Catégories avec fractionnement"
                    icon={<TagsOutlined style={{ color: "#fff", fontSize: 22 }} />}
                    iconBg="linear-gradient(135deg,#faad14 0%,#242166 100%)"
                  />
                </Col>
              </Row>

              {/* Tableau */}
              <Card
                bordered={false}
                style={{ borderRadius: 16, boxShadow: "0 4px 16px rgba(0,0,0,0.06)" }}
                bodyStyle={{ padding: 0 }}
              >
                <Table
                  columns={columns}
                  dataSource={fractionnements}
                  rowKey="fractionnementId"
                  pagination={{ pageSize: 10, showSizeChanger: true, showTotal: (t) => `${t} fractionnement(s)` }}
                  expandable={{
                    expandedRowRender: expandedTauxFractionnementRowRender,
                    expandRowByClick: true,
                  }}
                  style={{ borderRadius: 16, overflow: "hidden" }}
                />
              </Card>
            </div>
            <Footer />
          </div>
        </div>
      </div>
      <AddFractionnement
        show={showModal}
        handleClose={handleClose}
        editMode={editMode}
        selectedFractionnement={selectedFractionnement}
        onSaved={handleSaved}
      />
      <TauxFractionnement
        open={isModalOpenLine}
        setOpen={setIsModalOpenLine}
        tauxFractionnement={line}
        fractionnement={selectedFractionnement}
        edit={editMode}
      />
    </div>
  );
};

export default Fractionnements;